// Hook to check if user should see onboarding
import { useState, useEffect } from 'react';
import { supabase } from '@config/supabase';
import { useAuthStore } from '@stores/useAuthStore';

const ONBOARDING_DISMISSED_KEY = 'fyle_onboarding_dismissed';
const NEW_USER_THRESHOLD_DAYS = 7; // Consider user "new" if account created within last 7 days

export const useOnboarding = () => {
  const { user, isAuthenticated } = useAuthStore();
  const [isNewUser, setIsNewUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setIsLoading(false);
      return;
    }

    const checkOnboardingStatus = async () => {
      try {
        // Check if user has already dismissed onboarding
        const dismissed = localStorage.getItem(`${ONBOARDING_DISMISSED_KEY}_${user.id}`);
        if (dismissed === 'true') {
          setIsDismissed(true);
          setIsLoading(false);
          return;
        }

        // Fetch profile to check created_at
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('created_at')
          .eq('id', user.id)
          .single();

        if (error) {
          console.warn('⚠️ Failed to fetch profile for onboarding check:', error);
          setIsLoading(false);
          return;
        }

        if (profile?.created_at) {
          const createdDate = new Date(profile.created_at);
          const now = new Date();
          const daysSinceCreation = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24);
          
          setIsNewUser(daysSinceCreation <= NEW_USER_THRESHOLD_DAYS);
        }
      } catch (error) {
        console.error('❌ Error checking onboarding status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [user, isAuthenticated]);

  const dismissOnboarding = () => {
    if (user) {
      localStorage.setItem(`${ONBOARDING_DISMISSED_KEY}_${user.id}`, 'true');
      setIsDismissed(true);
    }
  };

  const shouldShowOnboarding = isNewUser && !isDismissed && !isLoading;

  return {
    shouldShowOnboarding,
    isNewUser,
    isDismissed,
    isLoading,
    dismissOnboarding,
  };
};





